import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import PageContainer from "../../components/PageContainer";
import ThemedTable from "../../components/ThemedTable";
import { apiClient } from "../../apiclient/apiclient";
import Loader from "../../components/Loader";
import { exportExpensesToExcel } from "../../utils/excelExporter";
import CreatableSelect from "react-select/creatable";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parse } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import {
  Plus,
  Search,
  Download,
  Trash2,
  Pencil,
  X,
  Calendar,
  ChevronDown,
} from "lucide-react";

// ─── Dummy Data ───────────────────────────────────────────────────────────────

const DEFAULT_CATEGORIES = [
  "Rent",
  "Salaries",
  "Logistics & Delivery",
  "Utilities",
  "Expired & Damaged Stock",
  "Marketing",
  "Packaging",
  "Others",
];

const STATUS_OPTIONS = ["Paid", "Pending"];

const PERIODS = [
  { label: "This Month",   value: "this_month" },
  { label: "Last Month",   value: "last_month" },
  { label: "This Quarter", value: "this_quarter" },
  { label: "This Year",    value: "this_year" },
];

const INITIAL_EXPENSES = [
  { id: 1, date: "01-08-2026", category: "Rent",                 description: "Warehouse rent – Aug",          amount: 25000, status: "Paid" },
  { id: 2, date: "02-08-2026", category: "Salaries",             description: "Staff salaries – Aug",          amount: 20000, status: "Paid" },
  { id: 3, date: "05-08-2026", category: "Logistics & Delivery", description: "Courier charges",               amount: 4200,  status: "Paid" },
  { id: 4, date: "06-08-2026", category: "Logistics & Delivery", description: "Last-mile delivery – Zone B",   amount: 3800,  status: "Pending" },
  { id: 5, date: "08-08-2026", category: "Packaging",            description: "Box & bubble wrap stock",       amount: 4500,  status: "Paid" },
  { id: 6, date: "10-08-2026", category: "Utilities",            description: "Electricity – Jul bill",        amount: 2500,  status: "Paid" },
  { id: 7, date: "11-08-2026", category: "Marketing",            description: "Instagram ads – Aug campaign",  amount: 2000,  status: "Paid" },
  { id: 8, date: "13-08-2026", category: "Others",               description: "Office supplies",               amount: 1200,  status: "Pending" },
  { id: 9, date: "13-08-2026", category: "Salaries",             description: "Delivery agent incentives",     amount: 3500,  status: "Pending" },
];

const CATEGORY_COLORS = {
  "Rent":                 "#172554",
  "Salaries":             "#F69314",
  "Logistics & Delivery": "#3b82f6",
  "Packaging":            "#10b981",
  "Utilities":            "#8b5cf6",
  "Marketing":            "#f43f5e",
  "Others":               "#94a3b8",
};

const PIE_COLORS = Object.values(CATEGORY_COLORS);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
  "₹ " + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const parseDDMMYYYY = (value) => {
  if (!value) return null;
  return parse(value, "dd-MM-yyyy", new Date());
};

const formatDDMMYYYY = (date) => {
  if (!date) return "";
  return format(date, "dd-MM-yyyy");
};

const EMPTY_FORM = { date: "", category: "Rent", description: "", amount: "", status: "Paid" };

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => (
  <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
    status === "Paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
  }`}>
    {status}
  </span>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const Expenses = () => {
  const [expenses, setExpenses]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [isSaving, setIsSaving]     = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [filterCat, setFilterCat]   = useState("All");
  const [period, setPeriod]         = useState("this_month");
  const [periodOpen, setPeriodOpen] = useState(false);
  const [showModal, setShowModal]   = useState(false);
  const [editingId, setEditingId]   = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [deleteId, setDeleteId]     = useState(null);
  const [from, setFrom]             = useState("");
  const [to, setTo]                 = useState("");
  const [sortBy, setSortBy]         = useState("date_desc");

  const selectedLabel = PERIODS.find((p) => p.value === period)?.label;

  // Derive unique categories from DEFAULT_CATEGORIES + database records
  const categoriesList = useMemo(() => {
    const dbCats = expenses.map((e) => e.category).filter(Boolean);
    return Array.from(new Set([...DEFAULT_CATEGORIES, ...dbCats])).sort();
  }, [expenses]);
  const selectOptions = useMemo(() => {
    return categoriesList.map((c) => ({ label: c, value: c }));
  }, [categoriesList]);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 2px #dbeafe" : "none",
      borderRadius: "0.375rem",
      fontSize: "0.875rem",
      minHeight: "40px",
      height: "40px",
      backgroundColor: "white",
      "&:hover": {
        borderColor: "#94a3b8"
      }
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 12px",
      height: "38px"
    }),
    input: (base) => ({
      ...base,
      margin: "0px",
      padding: "0px"
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "38px"
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "4px"
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: "4px"
    }),
    menu: (base) => ({
      ...base,
      fontSize: "0.875rem",
      borderRadius: "0.375rem",
      overflow: "hidden"
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    })
  };
  // Helper to resolve period value to YYYY-MM-DD date range
  const getPeriodDates = (periodVal) => {
    const today = new Date();
    let start = new Date();
    let end = new Date();

    if (periodVal === "this_month") {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (periodVal === "last_month") {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (periodVal === "this_quarter") {
      const quarter = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), quarter * 3, 1);
      end = new Date(today.getFullYear(), (quarter + 1) * 3, 0);
    } else if (periodVal === "this_year") {
      start = new Date(today.getFullYear(), 0, 1);
      end = new Date(today.getFullYear(), 11, 31);
    }

    const fmtYMD = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    return { start: fmtYMD(start), end: fmtYMD(end) };
  };

  // 1. Fetch Expenses from API
  const fetchExpenses = async (startDateStr, endDateStr) => {
    try {
      setLoading(true);
      
      const formatToYMD = (dStr) => {
        if (!dStr) return "";
        const parts = dStr.split("-");
        if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
        return "";
      };

      const start = formatToYMD(startDateStr || from);
      const end = formatToYMD(endDateStr || to);

      const res = await apiClient.get("/expenses", {
        params: { start, end },
      });

      // Format ISO Date strings from API back to DD-MM-YYYY for frontend display
      const formatted = res.data.map((exp) => {
        const d = new Date(exp.date);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return {
          ...exp,
          id: exp._id,
          date: `${day}-${month}-${year}`,
        };
      });
      setExpenses(formatted);
    } catch (err) {
      console.error("Failed to load expenses:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = () => {
    setActiveSearch(searchQuery);
    fetchExpenses();
  };

  useEffect(() => {
    const { start, end } = getPeriodDates(period);
    
    const formatToDDMMYYYY = (isoStr) => {
      if (!isoStr) return "";
      const parts = isoStr.split("-");
      if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
      return "";
    };

    const startDMY = formatToDDMMYYYY(start);
    const endDMY = formatToDDMMYYYY(end);

    setFrom(startDMY);
    setTo(endDMY);

    fetchExpenses(startDMY, endDMY);
  }, [period]);

  // ── Derived ──
  const filtered = useMemo(() => {
    const result = expenses.filter((e) => {
      const q = activeSearch.toLowerCase();
      const matchSearch =
        e.description.toLowerCase().includes(q) ||
        e.category.toLowerCase().includes(q);
      const matchCat = filterCat === "All" || e.category === filterCat;
      return matchSearch && matchCat;
    });

    // Apply Sorting
    return result.sort((a, b) => {
      if (sortBy === "date_desc") {
        const parseDate = (dStr) => {
          const parts = dStr.split("-");
          return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        };
        return parseDate(b.date) - parseDate(a.date);
      }
      if (sortBy === "date_asc") {
        const parseDate = (dStr) => {
          const parts = dStr.split("-");
          return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        };
        return parseDate(a.date) - parseDate(b.date);
      }
      if (sortBy === "amount_desc") {
        return b.amount - a.amount;
      }
      if (sortBy === "amount_asc") {
        return a.amount - b.amount;
      }
      return 0;
    });
  }, [expenses, activeSearch, filterCat, sortBy]);

  const totalExpenses  = expenses.reduce((s, e) => s + e.amount, 0);
  const paidExpenses   = expenses.filter((e) => e.status === "Paid").reduce((s, e) => s + e.amount, 0);
  const pendingExpenses = expenses.filter((e) => e.status === "Pending").reduce((s, e) => s + e.amount, 0);
  
  const largestCat = useMemo(() => {
    const categoriesMap = expenses.reduce((acc, e) => {
      acc[e.category] = (acc[e.category] || 0) + e.amount;
      return acc;
    }, {});
    return Object.entries(categoriesMap).sort((a, b) => b[1] - a[1])[0];
  }, [expenses]);

  // Chart data
  const categoryChartData = useMemo(() => {
    const categoriesMap = expenses.reduce((acc, e) => {
      acc[e.category] = (acc[e.category] || 0) + e.amount;
      return acc;
    }, {});
    return Object.entries(categoriesMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [expenses]);

  // ── Modal helpers ──
  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (exp) => {
    setEditingId(exp.id);
    setForm({
      date: exp.date,
      category: exp.category,
      description: exp.description,
      amount: exp.amount,
      status: exp.status,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  // 2. Save Expense (Create or Update)
  const saveExpense = async () => {
    if (!form.date || !form.description || !form.amount) {
      return alert("Please fill all required fields.");
    }
    try {
      setIsSaving(true);
      if (editingId) {
        await apiClient.put(`/expenses/${editingId}`, form);
      } else {
        await apiClient.post("/expenses", form);
      }
      fetchExpenses();
      closeModal();
    } catch (err) {
      console.error("Failed to save expense:", err);
      alert(err.response?.data?.message || "Failed to save expense");
    } finally {
      setIsSaving(false);
    }
  };

  // 3. Delete Expense
  const deleteExpense = async (id) => {
    try {
      await apiClient.delete(`/expenses/${id}`);
      fetchExpenses();
      setDeleteId(null);
    } catch (err) {
      console.error("Failed to delete expense:", err);
      alert("Failed to delete expense");
    }
  };

  // ── Input class ──
  const inp = "w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition";
  const lbl = "block text-sm font-medium text-slate-600 mb-1";

  return (
    <>
      <div className="bg-slate-50 min-h-screen">

        {/* ── HEADER ── */}
        <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Expenses</h1>
            <p className="text-sm text-slate-500 mt-1">Track and manage all business expenses</p>
          </div>
          <div className="flex items-center gap-3 pe-4">
            {/* Period Selector */}
            <div className="relative">
              <button
                onClick={() => setPeriodOpen((o) => !o)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 shadow-sm transition"
              >
                <Calendar size={15} className="text-slate-400" />
                {selectedLabel}
                <ChevronDown size={14} className="text-slate-400" />
              </button>
              {periodOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-100 rounded-xl shadow-lg z-10 w-44 overflow-hidden">
                  {PERIODS.map((p) => (
                    <button key={p.value} onClick={() => { setPeriod(p.value); setPeriodOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition hover:bg-gray-50 ${period === p.value ? "text-blue-700 font-semibold bg-blue-50" : "text-gray-700"}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={openAdd} className="px-4 py-2 bg-orange-500 text-white rounded-md text-sm font-medium hover:bg-orange-600 transition">
              Add Expense
            </button>
          </div>
        </div>

        {/* ── KPI CARDS ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 border-e border-b">
            <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
            <p className="text-sm opacity-80 relative z-10 font-semibold uppercase tracking-wider">Total Expenses</p>
            <h2 className="text-2xl font-bold mt-2 relative z-10">{fmt(totalExpenses)}</h2>
            <p className="text-sm opacity-80 mt-1 relative z-10 font-medium">{expenses.length} entries</p>
          </div>
          <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-emerald-500 to-green-600 border-e border-b">
            <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
            <p className="text-sm opacity-80 relative z-10 font-semibold uppercase tracking-wider">Paid</p>
            <h2 className="text-2xl font-bold mt-2 relative z-10">{fmt(paidExpenses)}</h2>
            <p className="text-sm opacity-80 mt-1 relative z-10 font-medium">{expenses.filter(e => e.status === "Paid").length} entries</p>
          </div>
          <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-orange-500 to-amber-600 border-e border-b">
            <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
            <p className="text-sm opacity-80 relative z-10 font-semibold uppercase tracking-wider">Pending</p>
            <h2 className="text-2xl font-bold mt-2 relative z-10">{fmt(pendingExpenses)}</h2>
            <p className="text-sm opacity-80 mt-1 relative z-10 font-medium">{expenses.filter(e => e.status === "Pending").length} entries</p>
          </div>
          <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-cyan-500 to-sky-600 border-b">
            <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40" />
            <p className="text-sm opacity-80 relative z-10 font-semibold uppercase tracking-wider">Top Category</p>
            <h2 className="text-lg font-bold mt-2 relative z-10 truncate">{largestCat?.[0] || "—"}</h2>
            <p className="text-sm opacity-80 mt-1 relative z-10 font-medium">{largestCat ? fmt(largestCat[1]) : "—"}</p>
          </div>
        </div>

        {/* ── CHARTS ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Bar chart by category */}
          <div className="bg-white border-b border-e p-4 flex flex-col">
            <div className="flex items-center justify-between mb-6 px-2">
              <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">Expenses by Category</h3>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{selectedLabel}</span>
            </div>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryChartData} barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#94a3b8", fontWeight: 600 }} axisLine={{ stroke: "#f1f5f9" }} tickLine={false} dy={8} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    cursor={{ fill: "#f8fafc" }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-2xl">
                          <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">{payload[0].payload.name}</p>
                          <p className="text-lg font-black text-[#172554]">{fmt(payload[0].value)}</p>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={36}>
                    {categoryChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={index % 2 === 0 ? "#172554" : "#F69314"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Pie chart */}
          <div className="bg-white border-b p-4 flex flex-col">
            <div className="flex items-center justify-between mb-6 px-2">
              <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">Expense Breakdown</h3>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">% Share</span>
            </div>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={categoryChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={48}>
                    {categoryChartData.map((entry, index) => (
                      <Cell key={`pie-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-[#172554] text-white rounded-lg px-4 py-3 shadow-2xl border border-white/10">
                          <p className="text-[10px] font-bold opacity-50 uppercase tracking-widest mb-1">{payload[0].name}</p>
                          <p className="text-base font-black">{fmt(payload[0].value)}</p>
                        </div>
                      );
                    }}
                  />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* ── EXPENSE TABLE ── */}
        <div className="bg-white border-b">


          {/* Table Control Panel (Light Blue) */}
          <div className="border-t border-b bg-[#EFF6FF] p-3">
            <div className="flex flex-row flex-wrap items-center gap-2.5 w-full">
              
              {/* 1. Search Bar */}
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <span className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                  <Search className="w-3.5 h-3.5 text-slate-400" />
                </span>
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearchSubmit();
                    }
                  }}
                  className="pl-8 pr-3 h-9 w-full text-sm bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition shadow-sm"
                />
              </div>

              {/* 2. Date Range Picker (From and To) */}
              <div className="flex items-center gap-2.5">
                {/* From Date */}
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-[#172554]">From</span>
                  <DatePicker
                    selected={parseDDMMYYYY(from)}
                    onChange={(date) => setFrom(formatDDMMYYYY(date))}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="DD-MM-YYYY"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    wrapperClassName="w-28"
                    className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-2 py-1 h-9 text-xs bg-white outline-none transition w-full shadow-sm text-center"
                  />
                </div>

                {/* To Date */}
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-[#172554]">To</span>
                  <DatePicker
                    selected={parseDDMMYYYY(to)}
                    onChange={(date) => setTo(formatDDMMYYYY(date))}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="DD-MM-YYYY"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    wrapperClassName="w-28"
                    className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-2 py-1 h-9 text-xs bg-white outline-none transition w-full shadow-sm text-center"
                  />
                </div>
              </div>

              {/* 3. Category Filter */}
              <select
                value={filterCat}
                onChange={(e) => setFilterCat(e.target.value)}
                className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-2.5 py-1 h-9 text-sm bg-white outline-none transition w-36 shadow-sm"
              >
                <option value="All">All Categories</option>
                {categoriesList.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>

              {/* 4. Sort Dropdown */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-2.5 py-1 h-9 text-sm bg-white outline-none transition w-40 shadow-sm"
              >
                <option value="date_desc">Date: Newest First</option>
                <option value="date_asc">Date: Oldest First</option>
                <option value="amount_desc">Amount: High to Low</option>
                <option value="amount_asc">Amount: Low to High</option>
              </select>

              {/* 5. Action Buttons (Search & Download) aligned to the right */}
              <div className="flex gap-2 items-center ml-auto">
                {/* Search Button */}
                <button
                  onClick={handleSearchSubmit}
                  disabled={loading}
                  className="w-9 h-9 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all shadow-sm hover:shadow-md active:scale-90 flex items-center justify-center shrink-0 disabled:opacity-50"
                  title="Search Date Range"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Search size={16} />
                  )}
                </button>

                {/* Download Button */}
                <button
                  onClick={() => exportExpensesToExcel(filtered, `${from} to ${to}`)}
                  className="w-9 h-9 bg-[#172554] text-white rounded-lg hover:bg-[#1e2f6a] transition-all shadow-sm hover:shadow-md active:scale-90 flex items-center justify-center shrink-0"
                  title="Export to Excel"
                >
                  <Download size={18} />
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <ThemedTable className="w-full text-[13px]">
              <thead>
                <tr>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Date</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Category</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider">Description</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider">Amount</th>
                  <th className="text-center px-4 py-3 text-xs uppercase tracking-wider">Status</th>
                  <th className="text-center px-4 py-3 text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10">
                      <Loader size={24} />
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-10 text-slate-400 text-sm">No expenses found.</td></tr>
                ) : (
                  filtered.map((exp) => (
                    <tr key={exp.id}>
                      <td className="text-slate-600 whitespace-nowrap">{exp.date}</td>
                      <td>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: CATEGORY_COLORS[exp.category] || "#94a3b8" }} />
                          <span className="text-slate-700 font-medium">{exp.category}</span>
                        </span>
                      </td>
                      <td className="text-slate-600">{exp.description}</td>
                      <td className="text-right font-semibold text-slate-800">{fmt(exp.amount)}</td>
                      <td className="text-center"><StatusBadge status={exp.status} /></td>
                      <td className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => openEdit(exp)} className="p-1.5 rounded-full border-2 border-[#172554] text-white bg-[#172554] hover:bg-white hover:text-[#172554] transition-colors">
                            <Pencil size={13} />
                          </button>
                          <button onClick={() => setDeleteId(exp.id)} className="p-1.5 rounded-full border-2 border-red-500 text-white bg-red-500 hover:bg-white hover:text-red-500 transition-colors">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {/* Totals */}
              {filtered.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-[#172554]/20 bg-blue-50">
                    <td colSpan={3} className="px-4 py-3 font-black text-[#172554] text-sm">Total ({filtered.length} entries)</td>
                    <td className="px-4 py-3 text-right font-black text-[#172554]">{fmt(filtered.reduce((s, e) => s + e.amount, 0))}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </ThemedTable>
          </div>
        </div>

      </div>

      {/* ── ADD / EDIT MODAL ── */}
      {showModal && createPortal(
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-2">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              saveExpense();
            }}
            className="bg-white rounded-xl shadow-lg w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* HEADER */}
            <div className="px-6 py-4 bg-[#172554] border-b flex justify-between items-center text-white">
              <h2 className="text-lg font-semibold">
                {editingId ? "Edit Expense" : "Add Expense"}
              </h2>
              <button type="button" onClick={closeModal} className="text-xl text-white">
                ✕
              </button>
            </div>

            {/* BODY */}
            <div className="p-6 space-y-4 flex-1 overflow-y-auto">
              
              {/* Row 1: Date & Category */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Date */}
                <div>
                  <label className="text-xs font-medium text-slate-700 block mb-1">Date <span className="text-red-400">*</span></label>
                  <DatePicker
                    selected={parseDDMMYYYY(form.date)}
                    onChange={(date) => setForm((f) => ({ ...f, date: formatDDMMYYYY(date) }))}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="DD-MM-YYYY"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    wrapperClassName="w-full"
                    className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="text-xs font-medium text-slate-700 block mb-1">Category</label>
                  <CreatableSelect
                    isClearable
                    options={selectOptions}
                    value={form.category ? { label: form.category, value: form.category } : null}
                    onChange={(newValue) => setForm((f) => ({ ...f, category: newValue ? newValue.value : "" }))}
                    styles={selectStyles}
                    placeholder="Select or type custom..."
                    menuPortalTarget={document.body}
                  />
                </div>
              </div>

              {/* Row 2: Description (Full Width) */}
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Description <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  placeholder="e.g. Warehouse rent – Aug"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                />
              </div>

              {/* Row 3: Amount & Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Amount */}
                <div>
                  <label className="text-xs font-medium text-slate-700 block mb-1">Amount (₹) <span className="text-red-400">*</span></label>
                  <input
                    type="number"
                    placeholder="0"
                    value={form.amount}
                    onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                    className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="text-xs font-medium text-slate-700 block mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                  >
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* FOOTER */}
            <div className="px-6 py-4 border-t bg-white flex justify-end gap-3">
              <button
                type="submit"
                disabled={isSaving}
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2 rounded-md transition-colors text-sm flex items-center gap-2 disabled:opacity-60"
              >
                {isSaving ? (
                  <Loader text={editingId ? "Saving..." : "Adding..."} inline />
                ) : (
                  editingId ? "Save Changes" : "Add Expense"
                )}
              </button>
              <button
                type="button"
                onClick={closeModal}
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}

      {/* ── DELETE CONFIRM ── */}
      {deleteId && createPortal(
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
            <div className="px-6 py-5">
              <h2 className="text-base font-bold text-slate-800 mb-1">Delete Expense?</h2>
              <p className="text-sm text-slate-500">This action cannot be undone.</p>
            </div>
            <div className="px-6 pb-5 flex justify-end gap-3">
              <button onClick={() => setDeleteId(null)} className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md transition">Cancel</button>
              <button onClick={() => deleteExpense(deleteId)} className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition">Delete</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Expenses;
